import React, {useEffect} from 'react'
import {observer} from 'mobx-react'
import PropTypes from 'prop-types';
import ConditionalComponent from "../../Shared/ConditionalComponent";
import {procedureNodeStyleEnum, procedureNodeTypeEnum} from '../../../serverClientShared/sharedEnums'
import {useStores} from "../../../Hooks";
import NodeInput from "../../Shared/NodeInput";
import FilesIndicators from "../../Shared/FilesIndicators";
import SelectionMenu from "./SelectionMenu";
import Draggable from "react-draggable";
import { isTablet} from "react-device-detect"
import TtsMuteButton from "../../Shared/TtsMuteButton";
import Utility from "../../../Utility";
import {getProcedureText, getNodeMedia} from "../../../serverClientShared/sharedUtils";
import {ObservableMap} from "mobx";

const ProcedureSummary = observer(props => {
    const {procedureStore, generalStore, editorStore} = useStores();

    useEffect(() => {
        return () => {
            procedureStore.shutUp();
        };
        // eslint-disable-next-line
    }, []);

    const {isPreview, fromEditor} = props;
    const currentNode = (isPreview
            ? procedureStore.currentPreviewedNode
            : procedureStore.currentNode
    ) || {};
    const currentProcedure = isPreview ? procedureStore.currentPreviewedProcedure : procedureStore.currentProcedure;

    useEffect(() => {
        if (!isPreview || !fromEditor || mobileNotTablet || props.position !== procedureNodeStyleEnum.FLOAT) {
            return
        }
        const currentEditedNode = editorStore.nodes.get(currentNode.Identifier);
        if(currentEditedNode) {
            currentEditedNode.data.nodeData.Style.FloatDefaultPosition = {
                HeaderX: procedureStore.headerX,
                HeaderY: procedureStore.headerY
            };
            editorStore.nodes.set(currentEditedNode.id, currentEditedNode);
            editorStore.nodes = new ObservableMap(editorStore.nodes);
            procedureStore.sendUpdateMessage();
            procedureStore.currentPreviewedProcedure.NestedNodes = editorStore.generateUpdatedNodes(editorStore.nodes);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [procedureStore.headerX, procedureStore.headerY]);


    const headerStyle = procedureNodeStyleEnum.TOP;
    const descriptionStyle =  procedureNodeStyleEnum.TOP;
    const connectionsStyle = currentNode?.Style?.Connection || procedureNodeStyleEnum.BOTTOM;

    let stepHeader = null, stepDescription = null, mediaFilesCount = null, inputParams = null, connectionsCount = 0;
    const mobileNotTablet = Utility.isMobile() && !isTablet;
    if (mobileNotTablet) {
        if (props.position === procedureNodeStyleEnum.TOP) {
            stepHeader = getProcedureText(currentNode, "StepHeader", currentProcedure);
        }
        if (props.position === procedureNodeStyleEnum.FLOAT) {
            stepDescription = getProcedureText(currentNode, "StepText", currentProcedure);
            mediaFilesCount = getNodeMedia(currentNode, "NodeMediaFilesRefs", currentProcedure).FileRefs;
            inputParams = currentNode.InputParams;
        }
        if (props.position === procedureNodeStyleEnum.BOTTOM && currentNode.ConnectionsData) {
            connectionsCount = currentNode.ConnectionsData.length;
        }
    } else {
        stepHeader = headerStyle === props.position ? getProcedureText(currentNode, "StepHeader", currentProcedure) : null;
        stepDescription = descriptionStyle === props.position ? getProcedureText(currentNode, "StepText", currentProcedure) : null;
        mediaFilesCount = descriptionStyle === props.position ? getNodeMedia(currentNode, "NodeMediaFilesRefs", currentProcedure).FileRefs.length : 0;
        inputParams = descriptionStyle === props.position ? currentNode.InputParams : null;

        if (currentNode.ConnectionsData) {
            connectionsCount = connectionsStyle === props.position ? currentNode.ConnectionsData.length : 0
        }
    }

    const noConnections = ![procedureNodeTypeEnum.MULTI, procedureNodeTypeEnum.QUESTION].includes(currentNode.NodeType) || !connectionsCount;

    const onlyConnections = !stepHeader && !stepDescription && !mediaFilesCount && !inputParams && !noConnections;

    const withDescription = !!stepDescription || !!inputParams;

    const shiftLeft = (!!getProcedureText(currentNode, "StepText", currentProcedure) || !!getNodeMedia(currentNode, "NodeMediaFilesRefs", currentProcedure).FileRefs.length || !!currentNode.InputParams) && descriptionStyle === procedureNodeStyleEnum.RIGHT && !mobileNotTablet;
    const shiftRight = (!!getProcedureText(currentNode, "StepText", currentProcedure) || !!getNodeMedia(currentNode, "NodeMediaFilesRefs", currentProcedure).FileRefs.length || !!currentNode.InputParams) && descriptionStyle === procedureNodeStyleEnum.LEFT && !mobileNotTablet;

    function getContentClassName() {
        let className = 'procedure-content-container';
        switch (props.position) {
            case procedureNodeStyleEnum.FLOAT:
                className += ' float';
                break;
            case procedureNodeStyleEnum.TOP:
                className += ' top';
                if (shiftLeft) {
                    className += ' shift-left'
                }
                if (shiftRight) {
                    className += ' shift-right'
                }
                break;
            case procedureNodeStyleEnum.BOTTOM:
                className += ' bottom';
                if (shiftLeft) {
                    className += ' shift-left'
                }
                if (shiftRight) {
                    className += ' shift-right'
                }
                break;
            case procedureNodeStyleEnum.RIGHT:
                className += ' right';
                break;
            case procedureNodeStyleEnum.LEFT:
                className += ' left';
                break;
            default:

        }

        const isWarning = currentNode.NodeType === procedureNodeTypeEnum.WARNING;
        if (isWarning) {
            className += ' warning-node';
        }
        if (onlyConnections) {
            className += ' only-connections';
        }
        if (withDescription) {
            className += ' with-description';
        }
        if (generalStore.isInFullscreen) {
            className += ' in-fullscreen';
        }
        return className;
    }

    function renderChild() {
        return (
            <div
                className={getContentClassName()}
                style={{background: currentNode.Color}}
            >
                <div className="step-text-container">
                    <ConditionalComponent if={!!stepHeader}>
                        <div className="node-title" dangerouslySetInnerHTML={{__html: stepHeader}}/>
                    </ConditionalComponent>
                    <ConditionalComponent if={!!stepDescription}>
                        <div
                            className={'node-description' + (generalStore.isInFullscreen ? ' in-fullscreen' : '')}
                            dangerouslySetInnerHTML={{__html: stepDescription}}
                        />
                        <ConditionalComponent if={!procedureStore.sessionViewerOpen}>
                            <TtsMuteButton isPreview={isPreview}/>
                        </ConditionalComponent>
                    </ConditionalComponent>
                    <ConditionalComponent if={!!inputParams}>
                        <NodeInput isPreview={isPreview}/>
                    </ConditionalComponent>
                    <ConditionalComponent if={withDescription && !!mediaFilesCount}>
                        <FilesIndicators isPreview={isPreview}/>
                    </ConditionalComponent>
                    <ConditionalComponent if={!noConnections}>
                        <SelectionMenu isPreview={isPreview}/>
                    </ConditionalComponent>
                </div>
            </div>
        )
    }

    if (!procedureStore.showSupportingText) {
        return null;
    }
    if (currentNode.NodeType === procedureNodeTypeEnum.END) {
        return null;
    }
    if (!stepHeader && !stepDescription && !inputParams && noConnections) {
        return null;
    }

    return !mobileNotTablet && props.position === procedureNodeStyleEnum.FLOAT ? (
        <Draggable
            bounds="parent"
            position={{x: procedureStore.headerX, y: procedureStore.headerY}}
            onStop={(e, data) => {
                const {x, y} = data;
                procedureStore.headerX = x;
                procedureStore.headerY = y;
            }}>
            {renderChild()}
        </Draggable>
    ) : (
        <>{renderChild()}</>
    );
});

ProcedureSummary.propTypes = {
    isPreview: PropTypes.bool.isRequired,
    position: PropTypes.string.isRequired
}

export default ProcedureSummary
